import {
  iOrder as iOrderType,
  Warehouse,
  Shelf as ShelfType,
  iField,
  iLine,
  CostCenter,
} from "/app/src/models";
import {
  costCenterService,
  iOrderService,
  shelfService,
  statusService,
  warehouseService,
} from "/app/src/services";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { DatePicker, Form, Input, Select } from "formik-antd";
import { Formik, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import Box from "/app/src/components/generic/components/box";
import FormikDisabler from "/app/src/components/generic/components/formikDisabler";
import { buildParams } from "/app/src/helpers";
import { Divider } from "@heroui/react";
import { useOrderFields } from "/app/src/components/apps/orderBuilder/settings/fields/orderFieldsData";
import { Moment } from "moment";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import DisabledSubmitButton from "/app/src/components/generic/components/buttons/DisabledSubmitButton";
import DisabledWrapper from "/app/src/components/generic/components/disabledWrapper";
import { SearchFilter } from "/app/src/components/generic/components/searchFilter";
import { Link } from "react-router-dom";
import { toast } from "sonner";

interface OrderField {
  name: string;
  values: string;
  required: boolean;
}

interface FormValues {
  shelves: string;
  name: string;
  statusId: number;
  [key: string]: string | number; // Allows dynamic fields from orderFields
}

/**
 * Displays the order details of an order created through the Order Builder App
 */
export default function OrderDetails({ iOrder }: { iOrder: iOrderType }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { orderFields, isOrderFieldsFetched } = useOrderFields(
    iOrder.integrationId,
    "order",
  );

  const { data: warehouses } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => {
      return warehouseService.getAll();
    },
    initialData: { warehouses: [] },
    select: (data: { warehouses: Warehouse[] }) => {
      return data.warehouses;
    },
  });

  const { data: costCenters } = useQuery({
    queryKey: ["costCenters"],
    queryFn: () => {
      return costCenterService.getAll();
    },
    initialData: { cost_centers: [] },
    select: (data: { cost_centers: CostCenter[] }) => data.cost_centers,
  });

  const mapCostCenters = useCallback((costCenter: CostCenter) => {
    return (
      <Select.Option key={costCenter.id} value={costCenter.id}>
        {costCenter.name}
      </Select.Option>
    );
  }, []);

  // Determine the forms initial values by looking at the iOrder.loadedData and then dynamically setting the values based on the keys in iOrder.loadedData
  const getInitialFormValues = (
    iOrder: iOrderType,
    orderFields: iField[],
  ): FormValues => {
    const initialValues: FormValues = {
      shelves: iOrder.loadedData?.shelves?.[0]?.toString() ?? "",
      name: iOrder.name || "",
      statusId: iOrder.statusId,
    };
    // Dynamically add fields from orderFields to initialValues
    orderFields.forEach((field: OrderField) => {
      if (iOrder.loadedData?.[field.name] !== undefined) {
        const value = iOrder.loadedData[field.name];
        // If value is an array of objects with 'id', extract the first id as string
        if (Array.isArray(value) && value.length > 0 && value[0]?.id) {
          initialValues[field.name] = value[0].id.toString();
        } else if (typeof value === "string" || typeof value === "number") {
          initialValues[field.name] = value;
        } else {
          initialValues[field.name] = "";
        }
      } else {
        initialValues[field.name] = "";
      }
    });
    return initialValues;
  };

  const formatForm = useCallback(
    (values: FormValues, lines?: iLine[]) => {
      // Filter out empty string values from the values object
      const filteredValues = Object.fromEntries(
        Object.entries(values).filter(([_, value]) => value !== ""),
      );
      const { statusId, ...valuesWithoutStatus } = filteredValues;
      if (iOrder.type === "Replenishment") {
        const data = {
          data: JSON.stringify({
            ...valuesWithoutStatus,
            shelves: [valuesWithoutStatus.shelves], // Ensure shelves is an array
          }),
          statusId,
        };
        return data;
      } else {
        const data = {
          data: JSON.stringify({
            // Convert all location types to arrays
            ...valuesWithoutStatus,
            lines,
          }),
          statusId,
        };
        return data;
      }
    },
    [iOrder.type],
  );

  // Gets all shelves
  const { data: shelves } = useQuery({
    queryKey: ["shelves"],
    queryFn: () => {
      return shelfService.getAll();
    },
    initialData: { shelves: [] },
    select: (data: { shelves: ShelfType[] }) => {
      return data.shelves;
    },
  });

  const { data: statuses } = useQuery(
    ["statuses", "iOrder", iOrder.id],
    () =>
      statusService
        .getAll(
          buildParams({
            integrationId: iOrder.integrationId,
            includeDefaults: true,
          }),
        )
        .then((response) => {
          if (iOrder.orderSent) {
            return response;
          }
          return {
            statuses: response.statuses.filter(
              (status) => status.updatableStatus === true,
            ),
          };
        }),
    {
      select: (data) => data.statuses,
      enabled: Boolean(iOrder?.integrationId),
      initialData: { statuses: [] },
    },
  );
  const saveOrder = useCallback(
    async (values: FormValues) => {
      if (values.statusId === 1 && iOrder.lines.length === 0) {
        toast.error(t("translation:cannot_send_to_power_pick"));
      } else {
        await iOrderService
          .updateSingle(iOrder.id, formatForm(values))
          .then((response) => {
            queryClient.setQueryData(["order", iOrder.id], response);
            queryClient.invalidateQueries(["revisions", String(iOrder.id)]);
          });
      }
    },
    [formatForm, iOrder.id, iOrder.lines.length, queryClient, t],
  );

  const shelvesId = iOrder?.loadedData?.shelves?.[0]?.toString() ?? "";
  const shelfObject = useMemo(() => {
    return shelves.find((shelf) => shelf.id === shelvesId) || {};
  }, [shelves, shelvesId]);

  const createDynamicOrderFieldsForm = useCallback(
    (values: FormValues, setFieldValue) => {
      /**
       * Dynamically create an onChange handler for date picker fields.
       * @param {string} fieldName - The name of the form field.
       * @returns {(value: Moment) => void} A function that sets the field value.
       */
      const getOnChangeDatePicker = (fieldName: string) => (value: Moment) => {
        setFieldValue(fieldName, value);
      };

      /**
       * Get the display value for a given field name or return a default label.
       * @param {string} fieldName - The name of the field to find.
       * @param {string} defaultLabel - The default label if field not found.
       * @returns {any} The values of the field or the default label.
       */
      const getNameFieldValue = (fieldName: string, defaultLabel) => {
        const field = orderFields.find((f) => f.name === fieldName);
        return field ? field.values : defaultLabel;
      };

      return (
        <>
          <Form.Item
            name="name"
            hasFeedback={false}
            label={getNameFieldValue("name", t("translation:name"))}
          >
            <Input name="name" size="large" />
          </Form.Item>
          {iOrder?.type === "Replenishment" && (
            <Form.Item
              hasFeedback={false}
              name="shelves"
              label={t("translation:shelf")}
            >
              <Select name="shelves" size="large">
                {shelves.map((shelf) => (
                  <Select.Option key={shelf.id} value={shelf.id}>
                    {shelf.storageUnitName}-{shelf.carrierName}/{shelf.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item
            hasFeedback={false}
            name="statusId"
            label={t("translation:status")}
          >
            <Select name="statusId" size="large">
              {statuses.map((status) => {
                return (
                  <Select.Option key={status.id} value={status.id}>
                    {status.name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          {orderFields
            .filter((field: OrderField) => field.name !== "name")
            .map((field: OrderField) => (
              <Form.Item
                hasFeedback={false}
                key={field.name}
                name={field.name}
                label={field.values}
              >
                {field.name === "warehouseName" ? (
                  <Select name={field.name} size="large">
                    {warehouses.map((warehouse) => (
                      <Select.Option key={warehouse.id} value={warehouse.name}>
                        {warehouse.name}
                      </Select.Option>
                    ))}
                  </Select>
                ) : field.name === "deadline" ? (
                  <DatePicker
                    name={field.name}
                    showTime
                    onChange={getOnChangeDatePicker(field.name)}
                    size="large"
                    style={{ width: "100%" }}
                  />
                ) : field.name === "costCenterId" ? (
                  <SearchFilter
                    name={field.name}
                    mapOptionsFn={mapCostCenters}
                    list={costCenters}
                  />
                ) : (
                  <Input name={field.name} size="large" />
                )}
              </Form.Item>
            ))}
        </>
      );
    },
    [
      costCenters,
      iOrder?.type,
      mapCostCenters,
      orderFields,
      shelves,
      statuses,
      t,
      warehouses,
    ],
  );

  const newOrderForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid, values, setFieldValue }) => (
        <Form layout="vertical">
          <div className="flex items-center justify-between">
            <div className="flex h-[32px] space-x-4">
              {iOrder.orderSent ? (
                <Link to={`/explorer/orders/${iOrder.powerpickId}`}>
                  <h2 className="text-[18px] text-primary-default mb-0 inline">
                    {iOrder.name}
                  </h2>
                </Link>
              ) : (
                <h2 className="text-[18px] mb-0 inline">{iOrder.name}</h2>
              )}
              <Divider orientation="vertical" />
              <h2 className="text-[18px] mb-0 inline">{iOrder.type}</h2>
              {iOrder.type === "Replenishment" && (
                <>
                  <Divider orientation="vertical" />
                  <h2 className="text-[18px] mb-0 inline truncate">
                    {shelfObject?.storageUnitName}
                    {shelfObject?.carrierName
                      ? `-${shelfObject.carrierName}`
                      : ""}
                    /{shelfObject?.name}
                  </h2>
                </>
              )}
            </div>
            <div className="flex items-center ">
              <DisabledSubmitButton
                type="primary"
                className="w-[160px]"
                disabled={!(dirty && isValid)}
              >
                {t("translation:save")}
              </DisabledSubmitButton>
            </div>
          </div>
          <Divider className="my-3" />
          <FormikDisabler disabled={iOrder.orderSent} />
          <DisabledWrapper disabled={iOrder?.orderSent}>
            <GridWrapper responsive={{ sm: 1, md: 1, lg: 3 }}>
              {createDynamicOrderFieldsForm(values, setFieldValue)}
            </GridWrapper>
          </DisabledWrapper>
        </Form>
      ),
      [
        createDynamicOrderFieldsForm,
        iOrder.name,
        iOrder.orderSent,
        iOrder.powerpickId,
        iOrder.type,
        shelfObject?.carrierName,
        shelfObject?.name,
        shelfObject?.storageUnitName,
        t,
      ],
    );

  return (
    <Box>
      {isOrderFieldsFetched ? (
        <Formik
          enableReinitialize
          component={newOrderForm}
          initialValues={getInitialFormValues(iOrder, orderFields)}
          onSubmit={saveOrder}
        />
      ) : (
        <div className="flex items-center justify-center h-full">
          <p>loading</p>
        </div>
      )}
    </Box>
  );
}
